import React, { useMemo, useState } from 'react';
import {
  Outlet,
  useLocation,
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { A } from 'TOMA';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import s from './ConnectorAdmin.module.scss';

interface iConnectorAdmin {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  company?: { id: number; name: string; title: string } | null;
}

function ConnectorAdmin(): JSX.Element {
  const { pathname } = useLocation();
  const { eventId } = useParams();
  const { eventRepository } = useApiContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reload, setReload] = useState(0);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const {
    response = [],
    loading,
    error,
  } = useRepo(
    async () => await eventRepository.getConnectorAdmins(eventId),
    [reload]
  );

  const allAdmins = (response ?? []) as iConnectorAdmin[];

  const admins = useMemo(() => {
    if (!search) return allAdmins;
    // Plain substring match (not a RegExp) so emails with '+', '.', etc. -
    // regex special characters - are matched literally instead of being
    // interpreted as a pattern.
    const needle = search.toLowerCase();
    return allAdmins.filter(
      (admin) =>
        (admin.firstname ?? '').toLowerCase().includes(needle) ||
        (admin.lastname ?? '').toLowerCase().includes(needle) ||
        (admin.email ?? '').toLowerCase().includes(needle) ||
        (admin.company?.name ?? '').toLowerCase().includes(needle)
    );
  }, [allAdmins, search]);

  const { response: moderatorResponse = [], loading: moderatorsLoading } =
    useRepo(
      async () => await eventRepository.getConnectorModerators(eventId),
      [reload]
    );

  const allModerators = (moderatorResponse ?? []) as iConnectorAdmin[];

  const moderators = useMemo(() => {
    if (!search) return allModerators;
    const needle = search.toLowerCase();
    return allModerators.filter(
      (m) =>
        (m.firstname ?? '').toLowerCase().includes(needle) ||
        (m.lastname ?? '').toLowerCase().includes(needle) ||
        (m.email ?? '').toLowerCase().includes(needle) ||
        (m.company?.name ?? '').toLowerCase().includes(needle)
    );
  }, [allModerators, search]);

  const removeModerator = async (moderatorId: number): Promise<void> => {
    setProcessingId(moderatorId);
    try {
      await eventRepository.removeConnectorModerator(eventId, moderatorId);
      toast(t('event:moderator_removed_from_event'), { type: 'success' });
      setReload((r) => r + 1);
    } catch (err: any) {
      toast(err.message, { type: 'error' });
    } finally {
      setProcessingId(null);
    }
  };

  const removeAdmin = async (adminId: number): Promise<void> => {
    setProcessingId(adminId);
    try {
      await eventRepository.removeConnectorAdmin(eventId, adminId);
      toast(t('event:admin_removed_from_event'), { type: 'success' });
      setReload((r) => r + 1);
    } catch (err: any) {
      toast(err.message, { type: 'error' });
    } finally {
      setProcessingId(null);
    }
  };

  const isAddView = pathname.includes('/connector-admin/add');

  return (
    <div className={s.Body}>
      <Box
        className={isAddView ? s.blur : undefined}
        sx={{ px: { xs: 2, md: 4 }, py: 3 }}
      >
        {/* Page header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: '22px', fontWeight: 700, color: '#111' }}
            >
              {t('event:connector')}
            </Typography>
            {!loading && (
              <Typography sx={{ fontSize: '13px', color: '#888', mt: 0.5 }}>
                {t('event:number_admins')}: {admins.length}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  routes.home2.path +
                    `events/${eventId ?? ''}/connector-admin/add`
                )
              }
              sx={{
                height: 40,
                fontSize: 13,
                borderColor: '#ddd',
                color: '#333',
                borderRadius: '8px',
                textTransform: 'none',
              }}
            >
              {t('event:add_admin')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  routes.home2.path +
                    `events/${eventId ?? ''}/connector-admin/add-moderator`
                )
              }
              sx={{
                height: 40,
                fontSize: 13,
                borderColor: '#ddd',
                color: '#333',
                borderRadius: '8px',
                textTransform: 'none',
              }}
            >
              {t('event:add_moderator')}
            </Button>
            <TextField
              size="small"
              placeholder={t('event:search')}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px', fontSize: 13, bgcolor: '#fff' },
              }}
              sx={{ width: 220 }}
            />
          </Box>
        </Box>

        {/* Error */}
        {error && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: '#fee2e2',
              borderRadius: 1,
              mb: 2,
              color: '#dc2626',
              fontSize: 13,
            }}
          >
            {error}
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: '#000' }} />
          </Box>
        )}

        {/* Table */}
        {!loading && (
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '10px',
              border: '1px solid #eee',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ overflowX: 'auto', width: '100%' }}>
              <table className={s.Table}>
                <thead>
                  <tr>
                    <th>{t('users:firstname')}</th>
                    <th>{t('users:lastname')}</th>
                    <th>{t('users:email')}</th>
                    <th>{t('event:company')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.firstname}</td>
                      <td>{admin.lastname}</td>
                      <td>{admin.email}</td>
                      <td>{admin.company?.name ?? '—'}</td>
                      <td>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={processingId === admin.id}
                          onClick={async () => await removeAdmin(admin.id)}
                          sx={{
                            fontSize: 12,
                            borderRadius: '6px',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t('event:remove_admin')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {admins.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: 'center', padding: 20 }}
                      >
                        {t('event:no_admin_found')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
        {/* Moderators table */}
        {!moderatorsLoading && (
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#111',
              mt: 4,
              mb: 1,
            }}
          >
            {t('event:number_moderators')}: {moderators.length}
          </Typography>
        )}
        {!moderatorsLoading && (
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '10px',
              border: '1px solid #eee',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ overflowX: 'auto', width: '100%' }}>
              <table className={s.Table}>
                <thead>
                  <tr>
                    <th>{t('users:firstname')}</th>
                    <th>{t('users:lastname')}</th>
                    <th>{t('users:email')}</th>
                    <th>{t('event:company')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {moderators.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.firstname}</td>
                      <td>{admin.lastname}</td>
                      <td>{admin.email}</td>
                      <td>{admin.company?.name ?? '—'}</td>
                      <td>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={processingId === admin.id}
                          onClick={async () => await removeModerator(admin.id)}
                          sx={{
                            fontSize: 12,
                            borderRadius: '6px',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t('event:remove_admin')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {moderators.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: 'center', padding: 20 }}
                      >
                        {t('event:no_moderator_found')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
      </Box>
      {isAddView && (
        <div className={s.Outlet}>
          <Link
            className={s.Outlet__close}
            to={routes.home2.path + `events/${eventId ?? ''}/connector-admin`}
            onClick={() => setReload((r) => r + 1)}
          >
            <div>
              <A.Icon icon="x-lg" />
            </div>
          </Link>
          <div className={s.Outlet__content}>
            <Outlet />
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectorAdmin;
